import { ChildProps } from '@apollo/client/react/hoc';
import React from 'react';
import I18n from 'i18n-js';
import {
  withCreateUserActionMutation,
  CoreUserActionsCreateMutationFn,
  useCreateUserAction,
} from '@reverbdotcom/commons/src/user_actions/create_action_mutation';
import { connect } from '@reverbdotcom/commons/src/connect';
import {
  UserAvailableActions,
  core_apimessages_UserActionName,
} from '@reverbdotcom/commons/src/gql/graphql';
import { RCGuidePopover } from '@reverbdotcom/cadence/components';
import type { PopoverAnchoring } from '@reverbdotcom/cadence/components';
import {
  trackDismissUserActionEvent,
  isUserActionAvailable,
  AVAILABLE_ACTION_NAME_TO_I18N_PREFIX,
  availableUserActionsQuery,
} from '@reverbdotcom/commons/src/components/user_actions_helpers';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

/**
 * Anchor the callout by EXACTLY ONE of:
 * - `anchorComponent`: an element cloned + owned as the popover anchor, or
 * - `anchorRef`: a ref to an element another component already renders (positioning-only).
 * Discriminated union → passing both, or neither, is a compile error.
 */
type CalloutAnchoring =
  | { anchorComponent: JSX.Element; anchorRef?: never }
  | { anchorComponent?: never; anchorRef: React.RefObject<HTMLElement> };

type ExternalProps = {
  parentComponentName: string;
  userActionName: core_apimessages_UserActionName;
  showCallout: boolean;
  experimentName?: string;
  width?: 'default' | 'narrow';
  content?: string | JSX.Element;
} & CalloutAnchoring;

interface MutationProps {
  createUserAction: CoreUserActionsCreateMutationFn;
}

type IProps = ChildProps<ExternalProps & MutationProps>;

export function UserActionCallout(props: IProps) {
  const user = useUser();
  const { data } = useQuery<UserAvailableActions.Query, UserAvailableActions.Variables>(
    availableUserActionsQuery,
    {
      errorPolicy: 'all',
      ssr: false,
      variables: {
        loggedOut: !!user.loggedOut,
      },
    },
  );

  const i18nPrefix = AVAILABLE_ACTION_NAME_TO_I18N_PREFIX[props.userActionName];
  const createUserActionState = useCreateUserAction(
    props.userActionName,
    data?.me,
    {
      listingId: null,
    },
    props.createUserAction,
  );

  const [isDismissed, setIsDismissed] = React.useState(false);

  const isActionAvailable = isUserActionAvailable(props.userActionName, data?.me?.availableActions);
  const showUserActionCallout: boolean = props.showCallout && isActionAvailable && !isDismissed;
  const expEnabled = props.experimentName ? isExperimentEnabled(user, props.experimentName) : true;

  // When there's nothing to show, render the wrapped anchor for the `anchorComponent` API, or
  // nothing at all in `anchorRef` mode (the trigger lives elsewhere and renders itself).
  if (!showUserActionCallout || !expEnabled) { return <>{props.anchorComponent ?? null}</>; }

  // Forward exactly one anchoring key. `props.anchorComponent` present ⇒ clone branch; otherwise the
  // union guarantees `anchorRef`. No cast, no runtime "which wins" — both-set is unrepresentable.
  const anchoring: PopoverAnchoring = props.anchorComponent
    ? { anchor: props.anchorComponent }
    : { anchorRef: props.anchorRef };

  return (
    <RCGuidePopover
      title={i18nPrefix ? I18n.t(`${i18nPrefix}.title`) : ''}
      content={props.content || (i18nPrefix ? I18n.t(`${i18nPrefix}.subtitle`) : '')}
      isOpen
      onDismiss={() => {
        setIsDismissed(true);
        createUserActionState.createUserAction();
        trackDismissUserActionEvent(props.parentComponentName, props.userActionName);
      }}
      {...anchoring}
      width={props.width || 'narrow'}
      position="bottom-end"
      preventAutoFocus
    />
  );
}

export default connect<ExternalProps>([
  withCreateUserActionMutation,
])(UserActionCallout);
